import { useCallback } from "react";
import { useNotification } from "../classes/contexts/NotificationContext";
import { useRemoteRenderContext } from "../classes/contexts/RemoteRenderContext";
import { MedicalImagesType } from "../classes/enums/MedicalImagesEnum";
import { SnackbarType } from "../classes/enums/SnackbarTypeEnum";
import { ActNatures } from "../classes/enums/SurgicalActsEnum";
import {
  MapViewNameToSpinesimSurgeryViewType,
  SpinesimSurgeryViewType,
} from "../classes/enums/ViewEnum";
import {
  addSurgicalActToDatabase,
  deleteSurgicalActFromDatabase,
  updateSurgicalActInDatabase,
} from "../services/SurgicalActService";

const useSurgicalActActions = currentPlanId => {
  const { client: remoteRendererClient } = useRemoteRenderContext();
  const { addNotification } = useNotification();

  const throwIfToolError = useCallback(
    (response, toolName) => {
      if (!response || !response.error) {
        return response;
      }

      const { code, message, details } = response.error;
      const finalMessage =
        message || `${toolName} update blocked by constraints.`;
      const error = new Error(finalMessage);
      error.code = code;
      error.details = details;
      error.isUserNotified = true;

      addNotification(SnackbarType.WARNING, finalMessage, {
        title: toolName,
        duration: 0,
      });

      throw error;
    },
    [addNotification]
  );

  const convertToDbFormat = (surgicalActData, planId) => {
    // Common fields for all acts.
    const base = {
      planId: planId,
      actNature: surgicalActData.actNature,
      vertebra: surgicalActData.vertebra,
      value: surgicalActData.value,
    };

    // Build the details object based on the actNature.
    let details = {};
    switch (surgicalActData.actNature) {
      case ActNatures.Osteotomy:
        details = {
          osteotomyType: surgicalActData.osteotomyType,
          supportPoints: surgicalActData.supportPoints,
        };
        break;
      case ActNatures.SegmentalTransformation:
        details = {
          rotation: surgicalActData.rotation,
          supportPointsSegmental: surgicalActData.supportPoints,
          type: surgicalActData.type,
          xShift: surgicalActData.xShift,
          yShift: surgicalActData.yShift,
        };
        break;
      case ActNatures.Cage:
        details = {
          cageHeight: surgicalActData.cageHeight,
          cagePosition: surgicalActData.cagePosition,
          cageWidth: surgicalActData.cageWidth,
          maxWidth: surgicalActData.maxWidth,
          minHeight: surgicalActData.minHeight,
          maxHeight: surgicalActData.maxHeight,
          rotationCage: surgicalActData.rotation,
          supportPointsCage: surgicalActData.supportPoints,
          type: surgicalActData.type,
          vertebraBelow: surgicalActData.vertebraBelow,
        };
        break;
      case ActNatures.GlobalAlignment:
        details = {
          globalAlignment: {
            pelvicTilt: surgicalActData.pelvicTilt,
            kneeAngle: surgicalActData.kneeAngle,
            lumbarLordosis: surgicalActData.lumbarLordosis,
            sacralSlope: surgicalActData.sacralSlope,
            globalTilt: surgicalActData.globalTilt,
            t1pa: surgicalActData.t1pa,
            piLl: surgicalActData.piLl,
            method: surgicalActData.method,
            targetPelvicTilt: surgicalActData.targetPelvicTilt,
            originalPelvicTilt:
              surgicalActData.originalPelvicTilt ??
              surgicalActData.pelvicTilt ??
              null,
            originalKneeAngle:
              surgicalActData.originalKneeAngle ??
              surgicalActData.kneeAngle ??
              null,
            originalLumbarLordosis:
              surgicalActData.originalLumbarLordosis ??
              surgicalActData.lumbarLordosis ??
              null,
            originalSacralSlope:
              surgicalActData.originalSacralSlope ??
              surgicalActData.sacralSlope ??
              null,
            originalGlobalTilt:
              surgicalActData.originalGlobalTilt ??
              surgicalActData.globalTilt ??
              null,
            originalTargetPelvicTilt:
              surgicalActData.originalTargetPelvicTilt ??
              surgicalActData.targetPelvicTilt ??
              null,
            originalT1pa:
              surgicalActData.originalT1pa ?? surgicalActData.t1pa ?? null,
            originalPiLl:
              surgicalActData.originalPiLl ?? surgicalActData.piLl ?? null,
          },
        };
        break;
      case ActNatures.Rod:
        details = {
          // Original rod fields
          rodPairId: surgicalActData.rodPairId,
          rodVertebraeSubset: surgicalActData.rodVertebraeSubset,
          rodDiameter: surgicalActData.rodDiameter,
          side: surgicalActData.side,

          // Flat rod specifications
          rodType: surgicalActData.rodType,
          rodManufacturer: surgicalActData.rodManufacturer,
          rodProduct: surgicalActData.rodProduct,
          rodExtraLength: surgicalActData.rodExtraLength,
          rodMaterial: surgicalActData.rodMaterial,
          rodCalculatedLength: surgicalActData.rodCalculatedLength,

          // Flat screw specifications
          screwType: surgicalActData.screwType,
          screwManufacturer: surgicalActData.screwManufacturer,
          screwProduct: surgicalActData.screwProduct,
          screwDiameter: surgicalActData.screwDiameter,
          screwLength: surgicalActData.screwLength,
          screwMaterial: surgicalActData.screwMaterial,
          screwCount: surgicalActData.screwCount,
          screws: surgicalActData.screws,
        };
        break;
      case ActNatures.ReciprocalChange:
        details = {
          reciprocalChange: {
            spineRigidityType: surgicalActData.spineRigidityType,
            warning: surgicalActData.warning,
          },
        };
        break;
      default:
        details = surgicalActData.details || {};
    }

    return {
      ...base,
      details,
    };
  };

  const deleteSurgicalAct = useCallback(async actToDelete => {
    try {
      if (!actToDelete._id) {
        return;
      }
      await deleteSurgicalActFromDatabase(actToDelete._id);
    } catch (error) {}
  }, []);

  /**
   * Unified synchronization function that handles all types of surgical acts
   * including global alignment.
   *
   * @param {Array} regularActs - Array of regular surgical acts
   * @param {Array} globalAlignmentAct - Array containing the global alignment act (if any)
   * @param {String} planId - The plan ID to sync with
   */
  const syncAllSurgicalActs = useCallback(
    async (regularActs, globalAlignmentAct, planId) => {
      try {
        if (!planId) {
          return;
        }

        // Combine regularActs and globalAlignmentAct for processing
        const allActs = [...(regularActs || [])];
        if (globalAlignmentAct && globalAlignmentAct.length > 0) {
          allActs.push(...globalAlignmentAct);
        }

        if (allActs.length === 0) {
          return;
        }

        // Process acts based on their status (new, modified)
        const newActs = allActs.filter(act => !act._id && !act._added);
        const modifiedActs = allActs.filter(act => act._id && act._modified);

        // Add new acts
        const createdActsWithIds = [];

        // Add new acts
        for (const act of newActs) {
          const response = await addSurgicalActToDatabase(
            convertToDbFormat(act, planId)
          );
          // Store the original act with its new ID
          if (response) {
            createdActsWithIds.push({
              originalAct: act,
              newId: response,
            });
          }

          act._added = true;
        }

        // Update modified acts
        for (const act of modifiedActs) {
          await updateSurgicalActInDatabase(
            convertToDbFormat(act, planId),
            act._id
          );
          delete act._modified; // Clear the modified flag after update
        }

        return createdActsWithIds;
      } catch (error) {}
    },
    []
  );

  // region VERTEBRAE SELECTION
  const startVertebraSelection = useCallback(
    async (viewType, tag, numberOfSelections) => {
      if (!currentPlanId) {
        return;
      }

      await remoteRendererClient.startVertebraSelection(
        currentPlanId,
        viewType,
        tag,
        numberOfSelections
      );
    },
    [remoteRendererClient, currentPlanId]
  );

  const stopVertebraSelection = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    await remoteRendererClient.stopVertebraSelection(currentPlanId);
  }, [remoteRendererClient, currentPlanId]);

  // region SURGERY

  const startSurgery = useCallback(
    async surgeryActs => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.StartSurgery(
        currentPlanId,
        MapViewNameToSpinesimSurgeryViewType(
          MedicalImagesType.LATERAL_SURGERY_DX
        ),
        surgeryActs
      );
      return response;
    },
    [remoteRendererClient, currentPlanId]
  );

  // region DECOMPENSATION

  const getInitialGlobalAlignment = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    const response =
      await remoteRendererClient.GetInitialGlobalAlignment(currentPlanId);
    return response;
  }, [remoteRendererClient, currentPlanId]);

  const updateGlobalAlignment = useCallback(
    async decompensationAttributes => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.UpdateGlobalAlignment(
        currentPlanId,
        decompensationAttributes
      );
      return response;
    },
    [remoteRendererClient, currentPlanId]
  );

  const performDecompensation = useCallback(
    async (
      pelvicTilt = null,
      kneeAngle = null,
      method = null,
      lumbarLordosis = null,
      sacralSlope = null,
      globalTilt = null,
      t1pa = null,
      targetPelvicTilt = null,
      piLl = null
    ) => {
      if (!currentPlanId) {
        return;
      }

      const defaultGlobalAlignmentData = {
        pelvicTilt: pelvicTilt,
        kneeAngle: kneeAngle,
        lumbarLordosis: lumbarLordosis,
        sacralSlope: sacralSlope,
        globalTilt: globalTilt,
        t1pa: t1pa,
        targetPelvicTilt: targetPelvicTilt,
        piLl: piLl,
        method: method,
      };

      const decompensationResult =
        await remoteRendererClient.StartDecompensation(
          currentPlanId,
          MedicalImagesType.LATERAL_SURGERY_DX,
          defaultGlobalAlignmentData
        );
      return decompensationResult;
    },
    [remoteRendererClient, currentPlanId]
  );

  const HideGlobalAlignmentTool = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    await remoteRendererClient.HideGlobalAlignmentTool(currentPlanId);
  }, [remoteRendererClient, currentPlanId]);

  // region OSTEOTOMY

  const showOsteotomyTool = useCallback(
    async (osteotomySelectedLandmarks, selectedOsteotomyType) => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.showOsteotomyTool(
        currentPlanId,
        MapViewNameToSpinesimSurgeryViewType(
          MedicalImagesType.LATERAL_SURGERY_DX
        ),
        osteotomySelectedLandmarks,
        selectedOsteotomyType
      );

      return response;
    },
    [remoteRendererClient, currentPlanId]
  );

  const hideOsteotomyTool = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    await remoteRendererClient.hideOsteotomyTool(currentPlanId);
  }, [remoteRendererClient, currentPlanId]);

  const updateOsteotomyToolValue = useCallback(
    async (vertebraId, osteotomyValue) => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.updateOsteotomyToolValue(
        currentPlanId,
        SpinesimSurgeryViewType.ALL,
        vertebraId,
        osteotomyValue
      );
      return response;
    },
    [remoteRendererClient, currentPlanId]
  );

  const getOsteotomySupportValues = useCallback(
    async vertebraId => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.getOsteotomySupportValues(
        currentPlanId,
        SpinesimSurgeryViewType.ALL,
        vertebraId
      );
      return response;
    },
    [remoteRendererClient, currentPlanId]
  );

  // region SEGMENTAL TRANSFORMATION
  const showSegmentalTransformationTool = useCallback(
    async (
      vertebraId,
      selectedVertebraType,
      selectedSegmentalTransformationType
    ) => {
      if (!currentPlanId) {
        return;
      }

      const response =
        await remoteRendererClient.showSegmentalTransformationTool(
          currentPlanId,
          MapViewNameToSpinesimSurgeryViewType(
            MedicalImagesType.LATERAL_SURGERY_DX
          ),
          vertebraId,
          selectedVertebraType,
          selectedSegmentalTransformationType
        );

      return throwIfToolError(response, "Segmental transformation");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  const hideSegmentalTransformationTool = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    await remoteRendererClient.hideSegmentalTransformationTool(currentPlanId);
  }, [remoteRendererClient, currentPlanId]);

  const getSegmentalTransformationAttributes = useCallback(
    async vertebraId => {
      if (!currentPlanId) {
        return;
      }

      const response =
        await remoteRendererClient.getSegmentalTransformationAttributes(
          currentPlanId,
          SpinesimSurgeryViewType.ALL,
          vertebraId
        );
      return throwIfToolError(response, "Segmental transformation");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  const translateSegmentalTransformationTool = useCallback(
    async (direction, value) => {
      if (!currentPlanId) {
        return;
      }

      const response =
        await remoteRendererClient.translateSegmentalTransformationTool(
          currentPlanId,
          SpinesimSurgeryViewType.ALL,
          direction,
          value
        );
      return throwIfToolError(response, "Segmental transformation");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  const rotateSegmentalTransformationTool = useCallback(
    async angle => {
      if (!currentPlanId) {
        return;
      }

      const response =
        await remoteRendererClient.rotateSegmentalTransformationTool(
          currentPlanId,
          SpinesimSurgeryViewType.ALL,
          angle
        );
      return throwIfToolError(response, "Segmental transformation");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  //region CAGE
  const showCageTool = useCallback(
    async (vertebraId, selectedVertebraType) => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.showCageTool(
        currentPlanId,
        MapViewNameToSpinesimSurgeryViewType(
          MedicalImagesType.LATERAL_SURGERY_DX
        ),
        vertebraId,
        selectedVertebraType
      );

      return throwIfToolError(response, "Cage");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  const removeCagePreview = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    await remoteRendererClient.removeCagePreview(currentPlanId);
  }, [remoteRendererClient, currentPlanId]);

  const getCageAttributes = useCallback(
    async vertebraId => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.getCageAttributes(
        currentPlanId,
        SpinesimSurgeryViewType.ALL,
        vertebraId
      );
      return throwIfToolError(response, "Cage");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  const rotateCageTool = useCallback(
    async angle => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.rotateCageTool(
        currentPlanId,
        SpinesimSurgeryViewType.ALL,
        angle
      );
      return throwIfToolError(response, "Cage");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  const setHeightCageTool = useCallback(
    async value => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.setHeightCageTool(
        currentPlanId,
        SpinesimSurgeryViewType.ALL,
        value
      );
      return throwIfToolError(response, "Cage");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  const setWidthCageTool = useCallback(
    async value => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.setWidthCageTool(
        currentPlanId,
        SpinesimSurgeryViewType.ALL,
        value
      );
      return throwIfToolError(response, "Cage");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  const setCagePosition = useCallback(
    async position => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.setCagePosition(
        currentPlanId,
        SpinesimSurgeryViewType.ALL,
        position
      );
      return throwIfToolError(response, "Cage");
    },
    [remoteRendererClient, currentPlanId, throwIfToolError]
  );

  //region ROD
  const showRodTool = useCallback(
    async (rodPayload, spineType = null) => {
      if (!currentPlanId) {
        return;
      }

      const response = await remoteRendererClient.showRodTool(
        currentPlanId,
        rodPayload,
        spineType
      );

      return response;
    },
    [remoteRendererClient, currentPlanId]
  );

  const removeRodPreview = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    await remoteRendererClient.removeRodPreview(currentPlanId);
  }, [remoteRendererClient, currentPlanId]);

  const removeRodHardware = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    const response =
      await remoteRendererClient.removeRodHardware(currentPlanId);
    return response;
  }, [remoteRendererClient, currentPlanId]);

  const removeCageHardware = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    const response =
      await remoteRendererClient.removeCageHardware(currentPlanId);
    return response;
  }, [remoteRendererClient, currentPlanId]);

  const hideRodHardware = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    await remoteRendererClient.hideRodHardware(currentPlanId);
  }, [remoteRendererClient, currentPlanId]);

  const showRodHardware = useCallback(async () => {
    if (!currentPlanId) {
      return;
    }

    await remoteRendererClient.showRodHardware(currentPlanId);
  }, [remoteRendererClient, currentPlanId]);

  return {
    startSurgery,
    deleteSurgicalAct,
    syncAllSurgicalActs,
    startVertebraSelection,
    stopVertebraSelection,
    performDecompensation,
    getInitialGlobalAlignment,
    updateGlobalAlignment,
    HideGlobalAlignmentTool,
    showOsteotomyTool,
    hideOsteotomyTool,
    updateOsteotomyToolValue,
    getOsteotomySupportValues,
    showSegmentalTransformationTool,
    hideSegmentalTransformationTool,
    getSegmentalTransformationAttributes,
    translateSegmentalTransformationTool,
    rotateSegmentalTransformationTool,
    showCageTool,
    removeCagePreview,
    getCageAttributes,
    rotateCageTool,
    setHeightCageTool,
    setWidthCageTool,
    setCagePosition,
    showRodTool,
    removeRodPreview,
    removeRodHardware,
    removeCageHardware,
    hideRodHardware,
    showRodHardware,
  };
};

export default useSurgicalActActions;
